import React, { useMemo, useState } from 'react';
import type {
  CtAttributionSegment,
  CtCallRow,
  CtNumberRow,
  CtTone,
} from '../../growth-call-tracking/types';
import {
  CT_ATTRIBUTION,
  CT_CALLS,
  CT_METRICS,
  CT_NUMBER_POOL,
  CT_POOL_NOTE,
  CT_STATS,
} from '../../growth-call-tracking/mockData';
import {
  CT_HANDOFF_META,
  CT_OUTCOME_META,
  CT_SOURCE_META,
  CT_TABLE_FILTERS,
  GROWTH_CALL_TRACKING_META,
} from '../../growth-call-tracking/config';
import { Icon } from '../Icon/Icon';
import { Pagination } from '../Pagination/Pagination';
import './growth-call-tracking.css';

const TONE: Record<CtTone, { accent: string; tint: string }> = {
  success: { accent: 'var(--success)', tint: 'var(--success-tint)' },
  gold: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
  terra: { accent: 'var(--terracotta)', tint: 'var(--terracotta-tint)' },
  info: { accent: 'var(--info)', tint: 'var(--info-tint)' },
  iris: { accent: 'var(--ai-3)', tint: 'var(--ai-tint)' },
  neutral: { accent: 'var(--ink-3)', tint: 'var(--surface-mute)' },
};

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

function CtCard({
  children,
  className = '',
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
      style={style}
    >
      {children}
    </div>
  );
}

function CtPill({ label, tone }: { label: string; tone: CtTone }) {
  const t = TONE[tone];
  return (
    <span
      className="inline-flex items-center whitespace-nowrap rounded-[var(--r-pill)] px-2.5 py-1 text-[11.5px] font-semibold"
      style={{ background: t.tint, color: t.accent }}
    >
      {label}
    </span>
  );
}

function CtStatTile({
  label,
  value,
  suffix,
  sub,
  link,
  tone,
}: {
  label: string;
  value: string;
  suffix?: string;
  sub: string;
  link?: string;
  tone: CtTone;
}) {
  const accent = tone === 'neutral' ? 'var(--ink-0)' : TONE[tone].accent;
  return (
    <CtCard className="px-[18px] py-[15px]">
      <div className={eyebrowClass}>{label}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span
          className="font-[var(--font-display)] text-[28px] font-extrabold leading-none tracking-[-0.04em]"
          style={{ color: accent }}
        >
          {value}
        </span>
        {suffix ? (
          <span className="text-[13px] font-semibold" style={{ color: accent }}>
            {suffix}
          </span>
        ) : null}
      </div>
      {link ? (
        <button
          type="button"
          className="mt-2 inline-flex cursor-pointer items-center gap-1 border-0 bg-transparent p-0 text-[12.5px] font-semibold text-[var(--success)]"
        >
          {link}
          <Icon kind="arrow-r" size={13} stroke={2} />
        </button>
      ) : (
        <div className="mt-2 text-[12.5px] text-[var(--ink-3)]">{sub}</div>
      )}
    </CtCard>
  );
}

function CtAttribution({ segments = CT_ATTRIBUTION }: { segments?: CtAttributionSegment[] }) {
  const total = segments.reduce((acc, segment) => acc + segment.value, 0);

  return (
    <CtCard className="px-6 py-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="m-0 font-[var(--font-display)] text-[16px] font-bold leading-snug tracking-[-0.02em] text-[var(--ink-0)]">
            Call Attribution
          </h2>
          <p className="mt-1 text-[12.5px] text-[var(--ink-3)]">
            Where your ad-sourced calls came from · Last 30 days
          </p>
        </div>
        <span className="font-[var(--font-mono)] text-[12px] text-[var(--ink-3)]">
          {total} calls total
        </span>
      </div>

      <div className="mt-4 flex h-3.5 w-full overflow-hidden rounded-[var(--r-pill)] bg-[var(--surface-mute)]">
        {segments.map((segment) => (
          <div
            key={segment.key}
            className="h-full"
            style={{ flex: segment.value, background: TONE[segment.tone].accent }}
          />
        ))}
      </div>

      <div
        className="ct-attr-legend mt-4 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}
      >
        {segments.map((segment) => {
          const t = TONE[segment.tone];
          const pct = total > 0 ? Math.round((segment.value / total) * 100) : 0;
          return (
            <div
              key={segment.key}
              className="rounded-[var(--r-md)] px-4 py-3"
              style={{ background: t.tint }}
            >
              <div className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 rounded-[3px]"
                  style={{ background: t.accent }}
                />
                <span className="text-[11.5px] font-semibold" style={{ color: t.accent }}>
                  {segment.label}
                </span>
              </div>
              <div className="mt-1.5 flex items-baseline gap-1.5">
                <span
                  className="font-[var(--font-display)] text-[22px] font-bold tracking-[-0.03em]"
                  style={{ color: t.accent }}
                >
                  {segment.value}
                </span>
                <span className="text-[12px]" style={{ color: t.accent, opacity: 0.7 }}>
                  ({pct}%)
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </CtCard>
  );
}

function CtNumberPool({ rows = CT_NUMBER_POOL }: { rows?: CtNumberRow[] }) {
  const [expanded, setExpanded] = useState(false);
  const assigned = rows.filter((row) => row.status === 'assigned').length;
  const reserved = rows.filter((row) => row.status === 'reserved').length;
  const available = rows.length - assigned - reserved;

  return (
    <CtCard className="overflow-hidden">
      <button
        type="button"
        onClick={() => {
          setExpanded((prev) => !prev);
        }}
        className="flex w-full cursor-pointer items-center justify-between gap-3 border-0 bg-transparent px-6 py-[18px] text-left"
      >
        <div className="flex items-center gap-3.5">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-[var(--r-md)] bg-[var(--surface-mute)] text-[var(--ink-3)]">
            <Icon kind="phone" size={20} stroke={1.9} />
          </span>
          <div>
            <p className="font-[var(--font-display)] text-[15px] font-bold leading-snug tracking-[-0.02em] text-[var(--ink-0)]">
              Tracking Number Pool
            </p>
            <p className="mt-1 text-[12.5px] text-[var(--ink-3)]">
              {assigned} assigned · {reserved} reserved · {available} available ·{' '}
              {rows.length} total
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-[var(--ink-3)]">
          {expanded ? 'Hide numbers' : 'Show numbers'}
          <Icon
            kind="down"
            size={16}
            stroke={2}
            className="transition-transform duration-200"
            style={{ transform: expanded ? 'rotate(180deg)' : undefined }}
          />
        </span>
      </button>

      {expanded ? (
        <div className="elva-fade border-t border-[var(--cream-2)] px-6 py-5">
          <div className="flex items-start gap-2.5 rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--info)_30%,transparent)] bg-[var(--info-tint)] px-4 py-3">
            <Icon
              kind="info"
              size={16}
              stroke={2}
              className="mt-0.5 shrink-0 text-[var(--info)]"
            />
            <p className="text-[12.5px] leading-[1.5] text-[var(--ink-2)]">{CT_POOL_NOTE}</p>
          </div>

          <div className="mt-4 overflow-hidden rounded-[var(--r-md)] border border-[var(--cream-2)]">
            <div
              className="grid items-center gap-0 border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-4 py-[11px] text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--ink-3)]"
              style={{ gridTemplateColumns: 'minmax(150px,auto) 1fr 120px 110px 110px' }}
            >
              <div>Number</div>
              <div>Assigned To</div>
              <div className="text-center">Source</div>
              <div className="text-right">Calls / mo</div>
              <div className="text-right">Since</div>
            </div>
            {rows.map((row, index) => {
              const source = CT_SOURCE_META[row.source];
              const isReserved = row.status === 'reserved';
              return (
                <div
                  key={row.number}
                  className={[
                    'grid items-center gap-0 px-4 py-[13px]',
                    index < rows.length - 1 ? 'border-b border-[var(--cream-2)]' : '',
                    isReserved ? 'bg-[var(--surface-mute)]' : 'bg-[var(--cream-1)]',
                  ].join(' ')}
                  style={{ gridTemplateColumns: 'minmax(150px,auto) 1fr 120px 110px 110px' }}
                >
                  <div className="flex items-center gap-2 font-[var(--font-mono)] text-[13px] font-medium text-[var(--ink-1)]">
                    <span
                      className="inline-block h-1.5 w-1.5 rounded-full"
                      style={{
                        background: isReserved ? 'var(--gold)' : 'var(--success)',
                      }}
                    />
                    {row.number}
                  </div>
                  <div className="flex items-center text-[13px] text-[var(--ink-2)]">
                    {row.assignedTo}
                    {isReserved && row.reservedUntil ? (
                      <span className="ml-2 text-[11px] italic text-[var(--gold)]">
                        (released {row.reservedUntil})
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-center">
                    <CtPill label={source.shortLabel} tone={source.tone} />
                  </div>
                  <div
                    className="text-right font-[var(--font-mono)] text-[13px] font-semibold"
                    style={{ color: row.callsThisMonth > 0 ? 'var(--ink-1)' : 'var(--ink-4)' }}
                  >
                    {row.callsThisMonth}
                  </div>
                  <div className="text-right text-[12px] text-[var(--ink-3)]">
                    {row.assignedDate}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </CtCard>
  );
}

function CtCallsTable({
  rows,
  query,
  onQuery,
  onExportCsv,
  exportLoading = false,
  pagination,
  onPageChange,
}: {
  rows: CtCallRow[];
  query: string;
  onQuery: (value: string) => void;
  onExportCsv?: () => void;
  exportLoading?: boolean;
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  onPageChange?: (page: number) => void;
}) {
  const totalCount = pagination?.totalCount ?? CT_METRICS.totalCalls;
  const hasSearch = query.trim().length > 0;
  const rangeStart = pagination ? (pagination.page - 1) * pagination.pageSize + 1 : 1;
  const rangeEnd = pagination
    ? Math.min(pagination.page * pagination.pageSize, pagination.totalCount)
    : rows.length;
  const summaryLabel = hasSearch
    ? `${rows.length} matching on this page · ${totalCount} total`
    : pagination
      ? `${rangeStart}–${rangeEnd} of ${totalCount}`
      : `${rows.length} of ${totalCount}`;
  return (
    <CtCard className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 border-b border-[var(--cream-2)] px-6 py-[18px]">
        <div>
          <h2 className="m-0 font-[var(--font-display)] text-[16px] font-bold leading-snug tracking-[-0.02em] text-[var(--ink-0)]">
            Recent Calls
          </h2>
          <p className="mt-1 text-[12.5px] text-[var(--ink-3)]">
            Ad-attributed inbound calls · Full conversation details in AI Receptionist
          </p>
        </div>
        <button
          type="button"
          onClick={onExportCsv}
          disabled={!onExportCsv || exportLoading}
          className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 text-[12.5px] font-semibold text-[var(--ink-2)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Icon kind="download" size={15} stroke={1.9} />
          {exportLoading ? 'Exporting…' : 'Download CSV'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 border-b border-[var(--cream-2)] bg-[var(--surface-mute)] px-6 py-3.5">
        <label className="relative w-60">
          <Icon
            kind="search"
            size={15}
            stroke={1.9}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-4)]"
          />
          <input
            value={query}
            onChange={(event) => {
              onQuery(event.target.value);
            }}
            placeholder="Search by caller phone..."
            className="h-9 w-full rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] pl-9 pr-3 text-[12.5px] text-[var(--ink-1)] outline-none"
          />
        </label>

        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {CT_TABLE_FILTERS.map((filter) => (
            <button
              key={filter}
              type="button"
              className="inline-flex h-9 cursor-pointer items-center gap-1.5 rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 text-[12.5px] font-medium text-[var(--ink-2)]"
            >
              {filter === 'Last 30 days' ? (
                <Icon kind="calendar" size={14} stroke={1.9} className="text-[var(--ink-3)]" />
              ) : null}
              {filter === 'Source' ? (
                <Icon kind="filter" size={14} stroke={1.9} className="text-[var(--ink-3)]" />
              ) : null}
              {filter === 'Campaign' ? (
                <Icon kind="grid" size={14} stroke={1.9} className="text-[var(--ink-3)]" />
              ) : null}
              {filter === 'Outcome' ? (
                <Icon kind="check-circ" size={14} stroke={1.9} className="text-[var(--ink-3)]" />
              ) : null}
              {filter === 'Handoff' ? (
                <Icon kind="team" size={14} stroke={1.9} className="text-[var(--ink-3)]" />
              ) : null}
              {filter}
              <Icon kind="down" size={14} stroke={1.9} className="text-[var(--ink-4)]" />
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1020px] border-collapse">
          <thead>
            <tr className="border-b border-[var(--cream-2)] text-left text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--ink-3)]">
              <th className="px-6 py-3 font-semibold">Caller</th>
              <th className="px-6 py-3 font-semibold">Time · Duration</th>
              <th className="px-6 py-3 font-semibold">Source · Campaign</th>
              <th className="px-6 py-3 font-semibold">Outcome</th>
              <th className="px-6 py-3 font-semibold">Handoff</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const source = CT_SOURCE_META[row.source];
              const outcome = CT_OUTCOME_META[row.outcome];
              const handoff = CT_HANDOFF_META[row.handoff];
              return (
                <tr key={row.id} className="border-b border-[var(--cream-2)] last:border-b-0">
                  <td className="px-6 py-4 align-top">
                    <p className="text-[13.5px] font-semibold text-[var(--ink-0)]">
                      {row.callerName}
                    </p>
                    <p className="mt-1 font-[var(--font-mono)] text-[12px] text-[var(--ink-3)]">
                      {row.caller}
                    </p>
                  </td>
                  <td className="px-6 py-4 align-top">
                    <p className="text-[13px] text-[var(--ink-1)]">{row.time}</p>
                    <p className="mt-1 inline-flex items-center gap-1 font-[var(--font-mono)] text-[11.5px] text-[var(--ink-3)]">
                      <Icon kind="clock" size={13} stroke={1.9} />
                      {row.duration}
                    </p>
                  </td>
                  <td className="px-6 py-4 align-top">
                    <p className="text-[13px] text-[var(--ink-1)]">{row.campaign}</p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <CtPill label={source.shortLabel} tone={source.tone} />
                      <span className="font-[var(--font-mono)] text-[11px] text-[var(--ink-4)]">
                        → {row.number}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 align-top">
                    <CtPill label={outcome.label} tone={outcome.tone} />
                  </td>
                  <td className="px-6 py-4 align-top">
                    <CtPill label={handoff.label} tone={handoff.tone} />
                  </td>
                  <td className="px-6 py-4 text-right align-top">
                    <button
                      type="button"
                      title="View in AI Receptionist"
                      className="inline-flex cursor-pointer items-center justify-center rounded-[var(--r-sm)] border-0 bg-transparent p-1.5 text-[var(--ink-3)]"
                    >
                      <Icon kind="eye" size={16} stroke={1.9} />
                    </button>
                  </td>
                </tr>
              );
            })}

            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-[13px] text-[var(--ink-3)]">
                  No matching calls found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[var(--cream-2)] bg-[var(--surface-mute)] px-6 py-3.5">
        <span className="text-[12px] text-[var(--ink-3)]">Showing {summaryLabel} calls</span>
        {pagination && onPageChange && pagination.totalPages > 1 ? (
          <Pagination
            page={pagination.page}
            total={pagination.totalPages}
            onChange={onPageChange}
            variant="compact"
          />
        ) : null}
      </div>
    </CtCard>
  );
}

export interface CallTrackingViewProps {
  items?: CtCallRow[];
  stats?: typeof CT_STATS;
  attribution?: CtAttributionSegment[];
  numberPool?: CtNumberRow[];
  pagination?: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  loading?: boolean;
  onPageChange?: (page: number) => void;
  onExportCsv?: () => void;
  exportLoading?: boolean;
  onPlayRecording?: (callId: string) => void;
}

export function CallTrackingView({
  items: itemsProp,
  stats: statsProp,
  attribution,
  numberPool,
  pagination,
  loading = false,
  onPageChange,
  onExportCsv,
  exportLoading = false,
  onPlayRecording: _onPlayRecording,
}: CallTrackingViewProps = {}) {
  const meta = GROWTH_CALL_TRACKING_META;
  const controlled = itemsProp !== undefined;
  const sourceCalls = controlled ? itemsProp : CT_CALLS;
  const stats = controlled ? (statsProp ?? []) : (statsProp ?? CT_STATS);
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return sourceCalls;
    return sourceCalls.filter(
      (row) =>
        row.callerName.toLowerCase().includes(normalized) ||
        row.caller.toLowerCase().includes(normalized) ||
        row.campaign.toLowerCase().includes(normalized),
    );
  }, [query, sourceCalls]);

  const handlePageChange = (nextPage: number) => {
    setQuery('');
    onPageChange?.(nextPage);
  };

  return (
    <div className="growth-call-tracking-root relative mx-auto w-full max-w-[1040px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-[22px]">
        <div className="mb-2 flex items-center gap-2">
          <Icon kind="phone" size={13} stroke={2} className="text-[var(--ai-3)]" />
          <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
            {meta.eyebrow}
          </span>
        </div>
        <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
          {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
        </h1>
        <p className="mt-2.5 max-w-[720px] text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
          {meta.description}
          {loading ? ' · Updating…' : ''}
        </p>
      </div>

      <div
        className="ct-kpi-grid mb-5 grid gap-3"
        style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}
      >
        {stats.map((stat) => (
          <CtStatTile
            key={stat.key}
            label={stat.label}
            value={stat.value}
            sub={stat.sub}
            tone={stat.tone}
            {...(stat.suffix != null ? { suffix: stat.suffix } : {})}
            {...(stat.link != null ? { link: stat.link } : {})}
          />
        ))}
      </div>

      <div className="mb-5">
        <CtAttribution
          {...(controlled ? { segments: attribution ?? [] } : attribution ? { segments: attribution } : {})}
        />
      </div>

      <div className="mb-5">
        <CtNumberPool
          {...(controlled ? { rows: numberPool ?? [] } : numberPool ? { rows: numberPool } : {})}
        />
      </div>

      <CtCallsTable
        rows={rows}
        query={query}
        onQuery={setQuery}
        {...(onExportCsv ? { onExportCsv } : {})}
        exportLoading={exportLoading}
        {...(pagination ? { pagination } : {})}
        {...(onPageChange ? { onPageChange: handlePageChange } : {})}
      />
    </div>
  );
}
